import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthCard from '../components/AuthCard.jsx';
import FormField from '../components/FormField.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { useFormState } from '../hooks/useFormState.js';
import { isValidEmail } from '../utils/validation.js';

function validate({ email, password }) {
  const errors = {};
  if (!isValidEmail(email)) errors.email = 'Please enter a valid email address.';
  if (!password)            errors.password = 'Password is required.';
  return errors;
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { values, fieldErrors, apiError, submitting, handleChange, setFieldErrors, setApiError, setSubmitting } =
    useFormState({ email: '', password: '' });

  const redirectTo = new URLSearchParams(location.search).get('redirect');

  useEffect(() => {
    if (!user) return;
    const dest = location.state?.from?.pathname
      ?? redirectTo
      ?? (user.roles?.includes('owner')     ? '/owner/dashboard'
        : user.roles?.includes('therapist') ? '/therapist/bookings'
        : '/');
    navigate(dest, { replace: true });
  }, [user, navigate, location, redirectTo]);

  async function handleSubmit(e) {
    e.preventDefault();
    const errors = validate(values);
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setSubmitting(true);
    try {
      await login(values);
    } catch (err) {
      setApiError(err.message ?? 'Sign in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Sign in"
      apiError={apiError}
      onSubmit={handleSubmit}
      footer={<>Don&apos;t have an account? <Link to={redirectTo ? `/signup?redirect=${encodeURIComponent(redirectTo)}` : '/signup'}>Create one</Link></>}
    >
      <FormField
        label="Email address" id="email" type="email" autoComplete="email"
        value={values.email} onChange={handleChange}
        error={fieldErrors.email} disabled={submitting}
      />
      <FormField
        label="Password" id="password" type="password" autoComplete="current-password"
        value={values.password} onChange={handleChange}
        error={fieldErrors.password} disabled={submitting}
      />
      <div className="auth-card__actions">
        <Link to="/forgot-password" className="auth-card__forgot">Forgot password?</Link>
        <button type="submit" className="btn btn--primary btn--full" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </div>
    </AuthCard>
  );
}
